<template>
  <div class="flex flex-col h-full bg-white">
    <!-- Success State -->
    <div v-if="submitted" class="flex flex-col items-center justify-center p-8 text-center h-full">
      <div class="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center text-white mb-6 shadow-xl">
        <i class="pi pi-check text-4xl"></i>
      </div>
      <h1 class="text-2xl font-bold text-surface-800 mb-2">הטופס נשלח בהצלחה!</h1>
      <p class="text-surface-600 leading-relaxed mb-8">
        הטופס הושלם ונשמר בהצלחה בתיק הלקוח.
      </p>
      <Button
        label="חזרה"
        icon="pi pi-arrow-right"
        class="w-full"
        severity="secondary"
        @click="onClose"
      />
    </div>

    <!-- Form -->
    <template v-else>
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
        <div class="flex items-center gap-3">
          <button
            class="p-2 -mr-2 text-primary-600 hover:bg-primary-50 rounded-full transition-colors"
            @click="onClose"
          >
            <i class="pi pi-arrow-right text-xl"></i>
          </button>
          <h1 class="font-bold text-lg text-surface-800 truncate max-w-[200px] md:max-w-[400px]">
            {{ form.title }}
          </h1>
        </div>
      </div>

      <!-- Form Fields -->
      <div class="flex-1 overflow-y-auto bg-surface-50 pb-32">
        <div class="px-4 py-6 space-y-6 max-w-2xl mx-auto">
          <div v-for="el in form.elements" :key="el.id">
            <!-- Short Text -->
            <div v-if="el.type === 'short_text'">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <InputText
                :modelValue="formData[el.id] || ''"
                @update:modelValue="val => updateField(el.id, val)"
                :placeholder="el.helperText"
                class="w-full"
                :invalid="!!formErrors[el.id]"
              />
              <small v-if="formErrors[el.id]" class="text-red-500 text-xs mt-1">{{ formErrors[el.id] }}</small>
            </div>

            <!-- Checkboxes -->
            <div v-else-if="el.type === 'checkboxes'" class="space-y-3">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <label
                v-for="(opt, i) in el.options"
                :key="i"
                class="flex items-center gap-3 p-4 bg-white border border-surface-200 rounded-2xl cursor-pointer hover:border-primary-300 transition-colors shadow-sm"
              >
                <Checkbox
                  :modelValue="!!formData[`${el.id}_${opt}`]"
                  @update:modelValue="val => updateCheckbox(`${el.id}_${opt}`, val as boolean, el.id)"
                  :binary="true"
                />
                <span class="text-sm font-medium text-surface-700 flex-1">{{ opt }}</span>
              </label>
              <small v-if="formErrors[el.id]" class="text-red-500 text-xs mt-1">{{ formErrors[el.id] }}</small>
            </div>

            <!-- Multiple Choice -->
            <div v-else-if="el.type === 'multiple_choice'" class="space-y-3">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <label
                v-for="(opt, i) in el.options"
                :key="i"
                class="flex items-center gap-3 p-4 bg-white border border-surface-200 rounded-2xl cursor-pointer hover:border-primary-300 transition-colors shadow-sm"
              >
                <RadioButton
                  :modelValue="formData[el.id]"
                  @update:modelValue="val => updateField(el.id, val)"
                  :value="opt"
                  :name="el.id"
                />
                <span class="text-sm font-medium text-surface-700">{{ opt }}</span>
              </label>
              <small v-if="formErrors[el.id]" class="text-red-500 text-xs mt-1">{{ formErrors[el.id] }}</small>
            </div>

            <!-- Read Only -->
            <div v-else-if="el.type === 'read_only'" class="mb-4">
              <label class="block text-sm font-bold text-surface-800 mb-2">{{ el.label }}</label>
              <div class="bg-surface-100/50 p-5 rounded-2xl border border-surface-200 text-sm text-surface-600 leading-relaxed whitespace-pre-wrap">
                {{ el.content }}
              </div>
            </div>

            <!-- Signature -->
            <div v-else-if="el.type === 'signature'">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <SignaturePad
                :modelValue="formData[el.id] as string"
                :error="formErrors[el.id]"
                @update:modelValue="val => updateField(el.id, val)"
              />
              <small v-if="formErrors[el.id]" class="text-red-500 text-xs mt-1">{{ formErrors[el.id] }}</small>
            </div>

            <!-- Image -->
            <div v-else-if="el.type === 'image'">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <div v-if="formData[el.id] || imagePreviews[el.id]" class="relative inline-block">
                <img
                  :src="imagePreviews[el.id] || formData[el.id]"
                  class="max-h-56 rounded-2xl border border-surface-200"
                  :class="uploadingImages[el.id] ? 'opacity-60' : ''"
                  alt="תמונה"
                />
                <!-- The image is on its way to storage; only its URL is submitted. -->
                <div
                  v-if="uploadingImages[el.id]"
                  class="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/40"
                >
                  <i class="pi pi-spin pi-spinner text-2xl text-surface-600"></i>
                </div>
                <button
                  v-else
                  type="button"
                  class="absolute top-2 left-2 bg-white/90 text-red-500 rounded-full w-8 h-8 flex items-center justify-center shadow"
                  @click="clearImage(el.id)"
                >
                  <i class="pi pi-trash"></i>
                </button>
              </div>
              <label
                v-else
                class="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-2xl cursor-pointer hover:border-primary-300 transition-colors"
                :class="formErrors[el.id] ? 'border-red-400 bg-red-50' : 'border-surface-300'"
              >
                <i class="pi pi-image text-3xl text-surface-400"></i>
                <span class="text-sm text-surface-500">{{ el.helperText || 'לחצו להעלאת תמונה' }}</span>
                <input type="file" accept="image/*" class="hidden" @change="(e) => onImageSelected(el.id, e)" />
              </label>
              <small v-if="formErrors[el.id]" class="text-red-500 text-xs mt-1">{{ formErrors[el.id] }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="absolute bottom-0 w-full p-4 bg-white border-t border-surface-100 shadow-[0_-4px_20px_rgba(0,0,0,0.02)] z-10">
        <Button
          :label="isUploadingImages ? 'מעלה תמונה...' : 'שליחת טופס'"
          icon="pi pi-send"
          class="w-full !py-3 !text-lg !font-bold"
          :loading="submitting"
          :disabled="isUploadingImages"
          @click="handleSubmit"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useToast } from 'vue-toastification';
import type { IConsentForm } from '@torim-app/types';
import { ConsentFormElementTypeEnum } from '@torim-app/types';
import { ConsentFormsApi } from '@app/api/admin/consent-forms-api';
import SignaturePad from './SignaturePad.vue';

const props = defineProps<{
  form: IConsentForm;
  /** Files land under this customer when the form is filled on their behalf. */
  customerId?: string;
  onSubmit: (submission: {
    customerName: string;
    data: Record<string, any>;
    signatureDataUrl?: string;
  }) => Promise<void>;
  onClose: () => void;
}>();

const toast = useToast();

const formData = reactive<Record<string, any>>({});
const formErrors = reactive<Record<string, string>>({});
const submitting = ref(false);
const submitted = ref(false);

const updateField = (id: string, value: any) => {
  formData[id] = value;
  if (formErrors[id]) delete formErrors[id];
};

const updateCheckbox = (optId: string, value: boolean, parentId: string) => {
  formData[optId] = value;
  if (formErrors[parentId]) delete formErrors[parentId];
};

/** Fields currently being uploaded, so the picker can show progress and block submit. */
const uploadingImages = reactive<Record<string, boolean>>({});
/** Object URLs shown while an image uploads, keyed by field id. */
const imagePreviews = reactive<Record<string, string>>({});

const isUploadingImages = computed(() => Object.values(uploadingImages).some(Boolean));

/**
 * Uploads the picked image straight away and keeps only its URL in the answer — a photo
 * inlined as base64 would otherwise be megabytes of JSON on submit, and would be stored
 * that way. A local preview shows while the upload is in flight.
 */
const onImageSelected = async (id: string, event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;

  const previewUrl = URL.createObjectURL(file);
  imagePreviews[id] = previewUrl;
  uploadingImages[id] = true;
  if (formErrors[id]) delete formErrors[id];

  try {
    const label = props.form.elements.find((el) => el.id === id)?.label ?? '';
    formData[id] = await ConsentFormsApi.uploadFormImage(props.form._id!, file, label, props.customerId);
  } catch (error) {
    console.error('Failed to upload consent form image:', error);
    toast.error('העלאת התמונה נכשלה, נסו שוב');
    formData[id] = '';
  } finally {
    uploadingImages[id] = false;
    URL.revokeObjectURL(previewUrl);
    delete imagePreviews[id];
  }
};

const clearImage = (id: string) => {
  updateField(id, '');
  delete imagePreviews[id];
};

const handleSubmit = async () => {
  // An image still uploading has no URL yet — submitting now would drop it.
  if (isUploadingImages.value) return;

  // Clear errors
  Object.keys(formErrors).forEach((key) => delete formErrors[key]);

  let hasErrors = false;

  props.form.elements.forEach((el) => {
    if (!el.required || el.type === ConsentFormElementTypeEnum.ReadOnly) return;

    if (el.type === ConsentFormElementTypeEnum.Checkboxes) {
      const hasChecked = el.options?.some((opt) => formData[`${el.id}_${opt}`]);
      if (!hasChecked) {
        formErrors[el.id] = 'יש לסמן לפחות אפשרות אחת';
        hasErrors = true;
      }
    } else if (!formData[el.id]) {
      formErrors[el.id] = 'שדה חובה';
      hasErrors = true;
    }
  });

  if (hasErrors) return;

  submitting.value = true;
  try {
    // Find the customer name from the first short_text element
    const nameEl = props.form.elements.find(
      (el) => el.type === ConsentFormElementTypeEnum.ShortText,
    );
    const customerName = nameEl ? (formData[nameEl.id] as string) || 'לא צוין' : 'לא צוין';

    // Find signature data
    const sigEl = props.form.elements.find(
      (el) => el.type === ConsentFormElementTypeEnum.Signature,
    );
    const signatureDataUrl = sigEl ? (formData[sigEl.id] as string) : undefined;

    await props.onSubmit({
      customerName,
      data: { ...formData },
      signatureDataUrl,
    });
    submitted.value = true;
  } finally {
    submitting.value = false;
  }
};
</script>
